import React from "react";
import styled, { keyframes } from "styled-components";

/* ─── Icônes inline SVG (pas de dépendance externe) ──────────────────────── */
const Icon = ({ name, active, color, size = 20 }) => {
  const stroke = color;
  const fill = active ? color : "none";
  const common = { width: size, height: size, viewBox: "0 0 24 24", fill: "none" };

  switch (name) {
    case "markets":
      return (
        <svg {...common}>
          <path d="M4 19V13M10 19V9M16 19V5M22 19v3" stroke={stroke} strokeWidth={active ? 2.4 : 2} strokeLinecap="round" strokeLinejoin="round" transform="translate(0,-3)" />
          <rect x="3" y="16" width="3" height="5" rx="1" fill={active ? stroke : "none"} stroke={stroke} strokeWidth="1.6" />
          <rect x="9" y="11" width="3" height="10" rx="1" fill={active ? stroke : "none"} stroke={stroke} strokeWidth="1.6" />
          <rect x="15" y="7" width="3" height="14" rx="1" fill={active ? stroke : "none"} stroke={stroke} strokeWidth="1.6" />
        </svg>
      );
    case "positions":
      return (
        <svg {...common}>
          <path d="M3 17l5-6 4 3 6-8" stroke={stroke} strokeWidth={active ? 2.4 : 2} strokeLinecap="round" strokeLinejoin="round" />
          <path d="M14 6h5v5" stroke={stroke} strokeWidth={active ? 2.4 : 2} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "wallet":
      return (
        <svg {...common}>
          <rect x="3" y="6" width="18" height="13" rx="2.5" stroke={stroke} strokeWidth={active ? 2.2 : 1.8} fill={active ? color + "22" : "none"} />
          <path d="M16 12.5h2.5" stroke={stroke} strokeWidth="2" strokeLinecap="round" />
          <path d="M3 9.5h18" stroke={stroke} strokeWidth="1.6" />
        </svg>
      );
    case "support":
      return (
        <svg {...common}>
          <path d="M4 13a8 8 0 0 1 16 0" stroke={stroke} strokeWidth={active ? 2.2 : 1.8} strokeLinecap="round" />
          <rect x="3" y="13" width="4" height="6" rx="2" stroke={stroke} strokeWidth={active ? 2.2 : 1.8} fill={active ? color + "22" : "none"} />
          <rect x="17" y="13" width="4" height="6" rx="2" stroke={stroke} strokeWidth={active ? 2.2 : 1.8} fill={active ? color + "22" : "none"} />
        </svg>
      );
    case "menu":
      return (
        <svg {...common}>
          <path d="M4 6h16M4 12h16M4 18h16" stroke={stroke} strokeWidth={active ? 2.4 : 2} strokeLinecap="round" />
        </svg>
      );
    case "lock":
      return (
        <svg {...common} width={size * 0.9} height={size * 0.9}>
          <rect x="5" y="11" width="14" height="10" rx="2" stroke={stroke} strokeWidth="2" fill="none" />
          <path d="M8 11V7a4 4 0 0 1 8 0v4" stroke={stroke} strokeWidth="2" strokeLinecap="round" fill="none" />
        </svg>
      );
    default:
      return null;
  }
};

/* ─── Traductions ──────────────────────────────────────────────────────── */
const NAV_LABELS = {
  markets:   { fr:"Marchés",   en:"Markets",   es:"Mercados",   de:"Märkte",   pt:"Mercados",   ar:"الأسواق" },
  positions: { fr:"Positions", en:"Positions", es:"Posiciones", de:"Positionen", pt:"Posições", ar:"المراكز" },
  wallet:    { fr:"Portefeuille", en:"Wallet", es:"Billetera",  de:"Wallet",   pt:"Carteira",   ar:"المحفظة" },
  support:   { fr:"Support",   en:"Support",   es:"Soporte",    de:"Support",  pt:"Suporte",     ar:"الدعم" },
  menu:      { fr:"Menu",      en:"Menu",      es:"Menú",       de:"Menü",     pt:"Menu",        ar:"القائمة" },
};

/* Titres affichés en haut de l'écran verrouillé — "Mon Wallet", "Mes Tickets"... */
const LOCK_TITLES = {
  positions: { fr:"Mes Positions", en:"My Positions", es:"Mis Posiciones", de:"Meine Positionen", pt:"Minhas Posições", ar:"مراكزي" },
  wallet:    { fr:"Mon Wallet",    en:"My Wallet",     es:"Mi Wallet",      de:"Mein Wallet",      pt:"Minha Carteira",  ar:"محفظتي" },
  support:   { fr:"Mes Tickets",   en:"My Tickets",    es:"Mis Tickets",    de:"Meine Tickets",    pt:"Meus Tickets",    ar:"تذاكري" },
};

/* Messages contextuels par section */
const LOCK_LABELS = {
  positions: {
    fr:"Connectez-vous pour voir vos positions",
    en:"Log in to view your positions",
    es:"Inicia sesión para ver tus posiciones",
    de:"Melde dich an, um deine Positionen zu sehen",
    pt:"Faça login para ver suas posições",
    ar:"سجّل الدخول لعرض مراكزك",
  },
  wallet: {
    fr:"Connectez-vous pour voir votre wallet",
    en:"Log in to view your wallet",
    es:"Inicia sesión para ver tu wallet",
    de:"Melde dich an, um dein Wallet zu sehen",
    pt:"Faça login para ver sua carteira",
    ar:"سجّل الدخول لعرض محفظتك",
  },
  support: {
    fr:"Connectez-vous pour créer des tickets et suivre vos demandes auprès du support.",
    en:"Log in to create tickets and track your support requests.",
    es:"Inicia sesión para crear tickets y seguir tus solicitudes de soporte.",
    de:"Melde dich an, um Tickets zu erstellen und deine Support-Anfragen zu verfolgen.",
    pt:"Faça login para criar tickets e acompanhar suas solicitações de suporte.",
    ar:"سجّل الدخول لإنشاء تذاكر ومتابعة طلبات الدعم الخاصة بك.",
  },
};

const SIGNIN_LABELS = { fr:"Se connecter", en:"Sign in", es:"Iniciar sesión", de:"Anmelden", pt:"Entrar", ar:"تسجيل الدخول" };

function navLabel(key, lang) { return NAV_LABELS[key]?.[lang] ?? NAV_LABELS[key]?.fr ?? key; }
function lockTitle(key, lang) { return LOCK_TITLES[key]?.[lang] ?? LOCK_TITLES[key]?.fr ?? navLabel(key, lang); }
function lockMessage(key, lang) { return LOCK_LABELS[key]?.[lang] ?? LOCK_LABELS[key]?.fr ?? LOCK_LABELS.positions[lang] ?? LOCK_LABELS.positions.fr; }

const ITEMS = [
  { id: "markets",   icon: "markets",   labelKey: "markets",   auth: false },
  { id: "positions", icon: "positions", labelKey: "positions", auth: true },
  { id: "wallet",    icon: "wallet",    labelKey: "wallet",    auth: true },
  { id: "support",   icon: "support",   labelKey: "support",   auth: true },
  { id: "menu",      icon: "menu",      labelKey: "menu",      auth: false },
];

/* ─── Styled components ───────────────────────────────────────────────── */
const fadeUp = keyframes`from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}`;

const NavWrap = styled.nav`
  display: none;
  position: fixed;
  bottom: 0; left: 0; right: 0;
  z-index: 200;
  background: ${({theme}) => theme.bgCard};
  border-top: 1px solid ${({theme}) => theme.border};
  padding: .5rem 0 max(.5rem, env(safe-area-inset-bottom));
  animation: ${fadeUp} .25s ease;

  @media(max-width: 720px) {
    display: flex;
  }
`;

const NavItem = styled.button`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: .15rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: .2rem 0;
  position: relative;
  -webkit-tap-highlight-color: transparent;
`;

const IconWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: ${({$active}) => $active ? "scale(1.08)" : "scale(1)"};
  transition: transform .15s ease;
`;

const LockDot = styled.span`
  position: absolute;
  top: -2px;
  right: -6px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({theme}) => theme.textMuted};
  opacity: .7;
`;

const Label = styled.span`
  font-size: .62rem;
  font-weight: 500;
  color: ${({$active, theme}) => $active ? theme.green : theme.textMuted};
  white-space: nowrap;
`;

const ActiveDot = styled.span`
  position: absolute;
  top: 0;
  width: 20px;
  height: 2px;
  border-radius: 1px;
  background: ${({theme}) => theme.green};
`;

/* ─── Overlay "Connectez-vous" pour sections protégées ───────────────── */
const LockOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 150;
  background: ${({theme}) => theme.bg};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  animation: ${fadeUp} .2s ease;
`;

const LockTitle = styled.h2`
  font-family: ${({theme}) => theme.fontDisplay};
  font-size: 1.6rem;
  font-weight: 700;
  color: ${({theme}) => theme.text};
  margin-bottom: 1.5rem;
`;

const LockIconCircle = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: ${({theme}) => theme.bgCard};
  border: 1px solid ${({theme}) => theme.border};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: ${({theme}) => theme.gold || "#ffc107"};
`;

const LockMessage = styled.p`
  font-size: .9rem;
  color: ${({theme}) => theme.textMuted};
  margin-bottom: 1.5rem;
  max-width: 280px;
`;

const LockSignInBtn = styled.button`
  background: ${({theme}) => theme.green};
  color: #000;
  border: none;
  border-radius: 10px;
  padding: .85rem 2.5rem;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity .15s;
  &:hover { opacity: .9; }
`;

/* ─── Variante "carte inline" (Support-style, cf. capture 3) ──────────── */
const LockCardWrap = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 2rem;
`;

const LockCardPageTitle = styled.h1`
  font-family: ${({theme}) => theme.fontDisplay};
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: .3rem;
  span { color: ${({theme}) => theme.accent}; }
`;

const LockCardSubtitle = styled.p`
  font-size: .85rem;
  color: ${({theme}) => theme.textMuted};
  margin-bottom: 1.5rem;
`;

const LockCard = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  background: ${({theme}) => theme.bgCard};
  border: 1px solid ${({theme}) => theme.accent}44;
  border-radius: 12px;
  padding: 1.5rem;
  flex-wrap: wrap;

  @media(max-width: 560px) {
    flex-direction: column;
    text-align: center;
  }
`;

const LockCardIconCircle = styled.div`
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: ${({theme}) => theme.bg};
  border: 1px solid ${({theme}) => theme.border};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({theme}) => theme.gold || "#ffc107"};
`;

const LockCardText = styled.p`
  flex: 1;
  font-size: .85rem;
  color: ${({theme}) => theme.textDim};
  line-height: 1.55;
  min-width: 200px;
`;

const LockCardBtn = styled.button`
  background: ${({theme}) => theme.accent};
  color: #000;
  border: none;
  border-radius: 8px;
  padding: .65rem 1.4rem;
  font-size: .85rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: opacity .15s;
  &:hover { opacity: .88; }
`;

/* ════════════════════════════════════════════════════════════════════════
   COMPOSANT : LockedSection
   Affiche l'écran "connectez-vous" pour une section protégée.

   Props :
     - sectionKey : "positions" | "wallet" | "support"
     - lang       : code langue
     - onSignIn   : callback bouton connexion
     - variant    : "fullscreen" (Positions/Wallet, plein écran centré)
                  | "card"       (Support — bandeau inline sous un titre de page)
     - cardSubtitle : sous-titre optionnel affiché sous le titre en mode "card"
════════════════════════════════════════════════════════════════════════ */
export function LockedSection({
  sectionKey = "positions",
  titleKey,            // alias rétrocompatible
  lang = "fr",
  onSignIn,
  variant = "fullscreen",
}) {
  const key = sectionKey || titleKey || "positions";
  const title   = lockTitle(key, lang);
  const message = lockMessage(key, lang);
  const signin  = SIGNIN_LABELS[lang] ?? SIGNIN_LABELS.fr;

  if (variant === "card") {
    /* Style "Mes Tickets" — titre de page + bandeau carte avec message + bouton */
    const subtitleMap = {
      support: {
        fr:"Gérez vos demandes auprès du support.",
        en:"Manage your support requests.",
        es:"Gestiona tus solicitudes de soporte.",
        de:"Verwalte deine Support-Anfragen.",
        pt:"Gerencie suas solicitações de suporte.",
        ar:"إدارة طلبات الدعم الخاصة بك.",
      },
    };
    const subtitle = subtitleMap[key]?.[lang] ?? subtitleMap[key]?.fr ?? "";

    return (
      <LockCardWrap>
        <LockCardPageTitle>
          {title.split(" ")[0]} <span>{title.split(" ").slice(1).join(" ")}</span>
        </LockCardPageTitle>
        {subtitle && <LockCardSubtitle>{subtitle}</LockCardSubtitle>}
        <LockCard>
          <LockCardIconCircle>
            <Icon name="lock" color="currentColor" size={20} />
          </LockCardIconCircle>
          <LockCardText>{message}</LockCardText>
          <LockCardBtn onClick={onSignIn}>{signin}</LockCardBtn>
        </LockCard>
      </LockCardWrap>
    );
  }

  /* Variante par défaut : plein écran centré (Positions, Wallet) */
  return (
    <LockOverlay>
      <LockTitle>{title}</LockTitle>
      <LockIconCircle>
        <Icon name="lock" color="currentColor" size={26} />
      </LockIconCircle>
      <LockMessage>{message}</LockMessage>
      <LockSignInBtn onClick={onSignIn}>{signin}</LockSignInBtn>
    </LockOverlay>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   COMPOSANT PRINCIPAL : BottomNavBar
════════════════════════════════════════════════════════════════════════
Props :
  - activeRoute : "markets" | "positions" | "wallet" | "support" | "menu"
  - user        : objet user ou null/undefined si non connecté
  - lang        : code langue ("fr","en",...)
  - onNavigate(routeId) : callback appelé pour markets/positions/wallet/support
  - onOpenMenu()         : callback appelé pour le bouton menu
  - onLockedClick(routeId) : callback optionnel appelé si l'item est verrouillé
                              (sinon onNavigate est appelé normalement, et c'est
                              à l'écran cible d'afficher <LockedSection/>)
════════════════════════════════════════════════════════════════════════ */
export default function BottomNavBar({
  activeRoute,
  user,
  lang = "fr",
  onNavigate,
  onOpenMenu,
  onLockedClick,
}) {
  const handleClick = (item) => {
    if (item.id === "menu") {
      onOpenMenu?.();
      return;
    }
    const locked = item.auth && !user;
    if (locked && onLockedClick) {
      onLockedClick(item.id);
      return;
    }
    onNavigate?.(item.id);
  };

  return (
    <NavWrap>
      {ITEMS.map((item) => {
        const isMenu = item.id === "menu";
        const active = !isMenu && activeRoute === item.id;
        const locked = item.auth && !user;

        return (
          <NavItem key={item.id} onClick={() => handleClick(item)}>
            <IconWrap $active={active}>
              <Icon
                name={item.icon}
                active={active}
                color={active ? "#00e676" : "#5a6a7a"}
                size={20}
              />
              {locked && <LockDot />}
            </IconWrap>
            <Label $active={active}>{navLabel(item.labelKey, lang)}</Label>
            {active && <ActiveDot />}
          </NavItem>
        );
      })}
    </NavWrap>
  );
}
